import useMapbox from '@/composables/useMapbox'
import { defineStore, storeToRefs } from 'pinia'
import { onMounted, ref, watch } from 'vue'
import { analyzeRecordChanges } from './analyzeRecordChanges'
import { getActiveLayers } from './getActiveLayers'
import _ from 'lodash'
import { useSelectionStore } from '../selectionNew'

type FeatureState = { color?: string; opacity?: number }
type FeatureStateRecord = Record<string, FeatureState>

export const useMapboxFeatureState = defineStore('useMapboxFeatureState', () => {
  const { map } = useMapbox()
  const { selectedEntityIds } = storeToRefs(useSelectionStore())
  const states = ref<FeatureStateRecord>({})

  watch(
    [states, selectedEntityIds],
    ([newState], [oldState]) => {
      fullReload(newState, oldState)
    },
    {
      deep: true,
      immediate: true
    }
  )

  onMounted(() => {
    map?.on('moveend', reload)
    map?.on('styledata', _.debounce(reload, 200))
    map?.on('sourcedata', _.debounce(reload, 200))
  })

  const pushRecord = (state: FeatureStateRecord) => {
    states.value = { ...states, ...state }
    reload()
  }

  const setRecords = (state: FeatureStateRecord) => {
    states.value = state
    reload()
  }

  const resetRecords = () => {
    states.value = {}
    reload()
  }

  const popRecords = (ids: string[]) => {
    states.value = Object.fromEntries(Object.entries(states).filter(([id]) => !ids.includes(id)))
  }

  function performStateRemoval(map: mapboxgl.Map | undefined, ids: string[]) {
    map?.queryRenderedFeatures(undefined, { layers: getActiveLayers(map) }).forEach((feature) => {
      if (ids.includes(feature.properties?.id)) {
        map?.removeFeatureState(feature)
      }
    })
  }

  function performStateAddition(
    map: mapboxgl.Map | undefined,
    state: Record<string, FeatureState>
  ) {
    if (map) {
      const layers = getActiveLayers(map)
      const features = map.queryRenderedFeatures(undefined, { layers })
      features.forEach((feature) => {
        const s = state[feature.properties?.id]
        if (s) {
          try {
            map?.setFeatureState(feature, s)
          } catch (e: any) {
            console.warn('Failed to set feature state', e?.message)
          }
        }
      })
    }
  }

  function reload() {
    if (!map) return
    performStateAddition(map, states.value)
    map.triggerRepaint()
  }

  function fullReload(newState: FeatureStateRecord, oldState: FeatureStateRecord | undefined) {
    if (map) {
      const result = analyzeRecordChanges(oldState ?? {}, newState)

      const idsForAddition = [...result.newEntries, ...result.entriesWithNewStates]
      const stateToAdd: Record<string, FeatureState> = {}

      idsForAddition.forEach((id) => {
        if (newState[id]) {
          stateToAdd[id] = newState[id]
        }
      })

      if (result.removedEntries.length > 0) {
        performStateRemoval(map, result.removedEntries)
      }

      if (Object.keys(stateToAdd).length > 0) {
        performStateAddition(map, stateToAdd)
      }

      map?.triggerRepaint()
    }
  }

  return {
    states,
    pushRecord,
    setRecords,
    resetRecords,
    popRecords
  }
})
